import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import dayjs from "dayjs";
import { HTTPClient } from "../../HTTPClient";
import { usePlace } from "../../providers/PlaceProvider";
import type { CompareApiData, HistoryEntry, InsightsPayload } from "./components/aiResults/types";

type GoogleInsightsContextValue = {
  history:                HistoryEntry[];
  compare:                CompareApiData | null;
  historyLoaded:          boolean;
  latestInsights:         InsightsPayload | null;
  latestInsightsLoaded:   boolean;
  keywordSetupExists:     boolean | null;
  keywordSetupLoaded:     boolean;
  refreshHistory:         () => void;
};

const GoogleInsightsContext = createContext<GoogleInsightsContextValue>({
  history:              [],
  compare:              null,
  historyLoaded:        false,
  latestInsights:       null,
  latestInsightsLoaded: false,
  keywordSetupExists:   null,
  keywordSetupLoaded:   false,
  refreshHistory:       () => {},
});

export function useGoogleInsights() {
  return useContext(GoogleInsightsContext);
}

export function GoogleInsightsProvider({ children }: { children: ReactNode }) {
  const { selectedPlace } = usePlace();
  const placeId = selectedPlace?.id;

  const [history,              setHistory]              = useState<HistoryEntry[]>([]);
  const [compare,              setCompare]              = useState<CompareApiData | null>(null);
  const [historyLoaded,        setHistoryLoaded]        = useState(false);
  const [latestInsights,       setLatestInsights]       = useState<InsightsPayload | null>(null);
  const [latestInsightsLoaded, setLatestInsightsLoaded] = useState(false);
  const [keywordSetupExists,   setKeywordSetupExists]   = useState<boolean | null>(null);
  const [keywordSetupLoaded,   setKeywordSetupLoaded]   = useState(false);

  const fetchHistory = (id: string) => {
    const endMonth   = dayjs().format("YYYY-MM");
    const startMonth = dayjs().subtract(6, "month").format("YYYY-MM");

    HTTPClient.get(`/stats/google/keyword-insights/history?placeId=${id}&startMonth=${startMonth}&endMonth=${endMonth}`)
      .then(({ data }) => {
        const snaps: HistoryEntry[] = (data?.snapshots ?? []).map((s: any) => ({
          month:          s.monthKey,
          globalScore:    s.globalScore,
          generatedAt:    s.generatedAt,
          categoryScores: s.categoryScores ?? {},
        }));
        setHistory(snaps);

        const m1Key = dayjs().subtract(1, "month").format("YYYY-MM");
        const m2Key = dayjs().subtract(2, "month").format("YYYY-MM");
        const m1 = data?.snapshots?.find((s: any) => s.monthKey === m1Key);
        const m2 = data?.snapshots?.find((s: any) => s.monthKey === m2Key);
        if (m1 && m2) {
          HTTPClient.get(`/stats/google/keyword-insights/compare?placeId=${id}&fromMonth=${m2Key}&toMonth=${m1Key}`)
            .then(({ data: cmp }) => setCompare(cmp))
            .catch(() => {});
        }

        // Pre-fetch the latest full insights so the AI tab redirects instantly
        const sorted = [...snaps].sort((a, b) => b.month.localeCompare(a.month));
        if (sorted.length > 0) {
          HTTPClient.get(`/stats/google/keyword-insights?placeId=${id}&monthKey=${sorted[0].month}`)
            .then(({ data: ins }) => {
              setLatestInsights({ ...ins, range: { startMonth: ins.monthKey, endMonth: ins.monthKey } });
            })
            .catch(() => {})
            .finally(() => setLatestInsightsLoaded(true));
        } else {
          setLatestInsightsLoaded(true);
        }
      })
      .catch(() => { setLatestInsightsLoaded(true); })
      .finally(() => setHistoryLoaded(true));
  };

  useEffect(() => {
    if (!placeId) return;
    setHistory([]);
    setCompare(null);
    setHistoryLoaded(false);
    setLatestInsights(null);
    setLatestInsightsLoaded(false);
    setKeywordSetupExists(null);
    setKeywordSetupLoaded(false);
    fetchHistory(placeId);
    HTTPClient.get(`/stats/google/keyword-setup?placeId=${placeId}`)
      .then(() => setKeywordSetupExists(true))
      .catch(() => setKeywordSetupExists(false))
      .finally(() => setKeywordSetupLoaded(true));
  }, [placeId]);

  const refreshHistory = () => {
    if (placeId) fetchHistory(placeId);
  };

  return (
    <GoogleInsightsContext.Provider value={{ history, compare, historyLoaded, latestInsights, latestInsightsLoaded, keywordSetupExists, keywordSetupLoaded, refreshHistory }}>
      {children}
    </GoogleInsightsContext.Provider>
  );
}
